import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Loader2,
  Phone,
  Plus,
  ReceiptText,
  Search,
  ShoppingBag,
  UserRound,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { menusApi, ordersApi } from '../../api';
import { getApiErrorMessage } from '../../api/axiosClient';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import getTranslatedField from '../../i18n/helpers/getTranslatedField';
import '../../styles/Orders/orders.css';

const DEFAULT_ESTABLISHMENT_ID = 1;
const SELECTED_MENU_STORAGE_KEY = 'selected_menu_id';
const ORDERS_PER_PAGE_OPTIONS = [10, 20, 50];

const translateFallback = (t, key, fallback) => {
  const value = t(key);

  return value === key ? fallback : value;
};


const formatNumber = (value, language) => {
  const number = Number(value || 0);

  try {
    return new Intl.NumberFormat(language).format(number);
  } catch {
    return String(number);
  }
};

const formatMoney = (value, currency, language) => {
  const number = Number(value || 0);
  const safeCurrency = currency || 'MAD';

  try {
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency: safeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  } catch {
    return `${number.toFixed(2)} ${safeCurrency}`;
  }
};

const formatDateTime = (value, language) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  try {
    return new Intl.DateTimeFormat(language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
};

const MANAGER_ROLES = [
  'restaurantmanager',
  'restaurantmanagerrole',
  'manager',
  'admin',
  'administrator',
  'superadmin',
  'superadministrator',
];

const WAITER_ROLES = ['waiter', 'serveur'];

const KITCHEN_ROLES = ['cuisine', 'kitchen', 'cuisiner', 'cuisinier'];

const ORDER_STATUS_KEYS = [
  'pending',
  'accepted',
  'preparing',
  'ready',
  'delivered',
  'cancelled',
];

const WORKFLOW_NAVIGATION_LABELS = {
  waiter: {
    all: 'Waiter queue',
    allDescription: 'Pending and ready orders',
    pending: 'Accept or cancel',
    ready: 'Deliver or cancel',
  },
  kitchen: {
    all: 'Kitchen queue',
    allDescription: 'Accepted and preparing orders',
    accepted: 'Start preparing or cancel',
    preparing: 'Mark ready or cancel',
  },
  manager: {
    all: 'All orders',
    allDescription: 'Full restaurant order workflow',
    pending: 'Waiting for validation',
    accepted: 'Accepted by the waiter',
    preparing: 'Being prepared in kitchen',
    ready: 'Ready to be delivered',
    delivered: 'Delivered to customer',
    cancelled: 'Cancelled orders',
  },
};

const normalizeRole = (role) => {
  return String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');
};

const getUserRole = (user) => {
  return normalizeRole(user?.role || user?.type || user?.profile?.role);
};

const getUserWorkflow = (user) => {
  const role = getUserRole(user);

  if (WAITER_ROLES.includes(role)) return 'waiter';
  if (KITCHEN_ROLES.includes(role)) return 'kitchen';
  if (MANAGER_ROLES.includes(role)) return 'manager';

  return 'manager';
};

const getVisibleStatusesByWorkflow = (workflow) => {
  if (workflow === 'waiter') return ['pending', 'ready'];
  if (workflow === 'kitchen') return ['accepted', 'preparing'];

  return ORDER_STATUS_KEYS;
};

const getWorkflowNavigationItems = (workflow, visibleStatuses, allStatusOptions, t) => {
  const labels = WORKFLOW_NAVIGATION_LABELS[workflow] || WORKFLOW_NAVIGATION_LABELS.manager;
  const statusOptionsByValue = allStatusOptions.reduce((options, option) => {
    return {
      ...options,
      [option.value]: option,
    };
  }, {});

  const allLabelKey = `orders.workflowNavigation.${workflow}.all`;
  const allDescriptionKey = `orders.workflowNavigation.${workflow}.allDescription`;

  return [
    {
      value: '',
      label: translateFallback(t, allLabelKey, labels.all),
      description: translateFallback(t, allDescriptionKey, labels.allDescription),
      icon: ShoppingBag,
    },
    ...visibleStatuses.map((status) => ({
      value: status,
      label: statusOptionsByValue[status]?.label || status,
      description: translateFallback(
        t,
        `orders.workflowNavigation.${workflow}.${status}`,
        labels[status] || labels.allDescription,
      ),
      icon: status === 'ready' || status === 'delivered' ? Check : ShoppingBag,
    })),
  ];
};

const getAllowedNextStatuses = (workflow, currentStatus) => {
  if (workflow === 'waiter') {
    if (currentStatus === 'pending') return ['pending', 'accepted', 'cancelled'];
    if (currentStatus === 'ready') return ['ready', 'delivered', 'cancelled'];

    return [currentStatus].filter(Boolean);
  }

  if (workflow === 'kitchen') {
    if (currentStatus === 'accepted') {
      return ['accepted', 'preparing', 'cancelled'];
    }

    if (currentStatus === 'preparing') {
      return ['preparing', 'ready', 'cancelled'];
    }

    return [currentStatus].filter(Boolean);
  }

  return ORDER_STATUS_KEYS;
};

const normalizeStatus = (status) => {
  const value = String(status || '').toLowerCase();

  return ORDER_STATUS_KEYS.includes(value) ? value : 'pending';
};

function CustomSelect({
  value,
  options = [],
  onChange,
  placeholder = '—',
  className = '',
  disabled = false,
  ariaLabel,
}) {
  const selectRef = useRef(null);
  const [open, setOpen] = useState(false);

  const selectedOption =
    options.find(
      (option) =>
        String(option.value) ===
        String(value),
    ) || null;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleOutsideClick,
    );
    document.addEventListener(
      'keydown',
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleOutsideClick,
      );
      document.removeEventListener(
        'keydown',
        handleEscape,
      );
    };
  }, []);

  const selectedLabel =
    selectedOption?.label ||
    placeholder ||
    options[0]?.label ||
    '—';

  return (
    <div
      ref={selectRef}
      className={`orders-custom-select ${className}`}
    >
      <button
        type="button"
        className={`orders-custom-select__button ${
          open ? 'is-open' : ''
        }`}
        onClick={() => {
          if (!disabled) {
            setOpen(
              (currentOpen) =>
                !currentOpen,
            );
          }
        }}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selectedLabel}</span>

        <ChevronDown
          size={16}
          className={
            open
              ? 'is-rotated'
              : ''
          }
        />
      </button>

      {open ? (
        <div
          className="orders-custom-select__menu"
          role="listbox"
        >
          {options.map((option) => {
            const active =
              String(option.value) ===
              String(value);

            return (
              <button
                key={
                  option.value ||
                  'empty-option'
                }
                type="button"
                role="option"
                aria-selected={active}
                className={
                  active
                    ? 'is-active'
                    : ''
                }
                onClick={() => {
                  onChange?.(
                    option.value,
                  );

                  setOpen(false);
                }}
              >
                <span>{option.label}</span>

                {active ? (
                  <Check size={15} />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function Orders() {
  const { t, language } = useLanguage();
  const { user } = useAuth();

  const direction = String(language || '')
    .toLowerCase()
    .startsWith('ar')
    ? 'rtl'
    : 'ltr';
  const { showAlert } = useAlerts();

  const [orders, setOrders] = useState([]);
  const [menus, setMenus] = useState([]);
  const [menusLoading, setMenusLoading] = useState(true);
  const [selectedMenuId, setSelectedMenuId] = useState(
    localStorage.getItem(SELECTED_MENU_STORAGE_KEY) || '',
  );
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    order_type: '',
  });
  const [loading, setLoading] = useState(true);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(10);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderLoading, setSelectedOrderLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  const userWorkflow = getUserWorkflow(user);
  const visibleStatuses = useMemo(
    () => getVisibleStatusesByWorkflow(userWorkflow),
    [userWorkflow],
  );
  const canCreateOrders = userWorkflow === 'manager' || userWorkflow === 'waiter';

  const establishmentId =
    localStorage.getItem('selected_establishment_id') ||
    DEFAULT_ESTABLISHMENT_ID;

  const selectedMenu = useMemo(
    () =>
      menus.find(
        (menu) => String(menu.id) === String(selectedMenuId),
      ) || null,
    [menus, selectedMenuId],
  );

  const allStatusOptions = useMemo(
    () => [
      { value: 'pending', label: t('orders.status.pending') },
      { value: 'accepted', label: t('orders.status.accepted') },
      { value: 'preparing', label: t('orders.status.preparing') },
      { value: 'ready', label: t('orders.status.ready') },
      { value: 'delivered', label: t('orders.status.delivered') },
      { value: 'cancelled', label: t('orders.status.cancelled') },
    ],
    [t, language],
  );

  const statusOptions = useMemo(() => {
    const visibleStatusOptions = allStatusOptions.filter((option) =>
      visibleStatuses.includes(option.value),
    );

    const allStatusesLabel =
      userWorkflow === 'waiter'
        ? `${t('orders.status.pending')} / ${t('orders.status.ready')}`
        : userWorkflow === 'kitchen'
          ? `${t('orders.status.accepted')} / ${t('orders.status.preparing')}`
          : t('orders.filters.allStatuses');

    return [{ value: '', label: allStatusesLabel }, ...visibleStatusOptions];
  }, [allStatusOptions, visibleStatuses, userWorkflow, t]);

  const typeOptions = useMemo(
    () => [
      { value: '', label: t('orders.filters.allTypes') },
      { value: 'dine_in', label: t('orders.types.dine_in') },
      { value: 'takeaway', label: t('orders.types.takeaway') },
      { value: 'delivery', label: t('orders.types.delivery') },
    ],
    [t],
  );

  const paginationSizeOptions = useMemo(
    () =>
      ORDERS_PER_PAGE_OPTIONS.map((pageSize) => ({
        value: String(pageSize),
        label: `${formatNumber(
          pageSize,
          language,
        )} / ${t(
          'orders.pagination.page',
        )}`,
      })),
    [t, language],
  );

  const getOrderStatusOptions = (order) => {
    const currentStatus = normalizeStatus(order.status);
    const allowedStatuses = getAllowedNextStatuses(userWorkflow, currentStatus);

    return allStatusOptions.filter((option) =>
      allowedStatuses.includes(option.value),
    );
  };

  const orderCountByStatus = useMemo(() => {
    return visibleStatuses.reduce((statusCounts, status) => {
      return {
        ...statusCounts,
        [status]: orders.filter(
          (order) => normalizeStatus(order.status) === status,
        ).length,
      };
    }, {});
  }, [orders, visibleStatuses]);

  const visibleOrdersCount = useMemo(() => {
    return visibleStatuses.reduce(
      (total, status) => total + Number(orderCountByStatus[status] || 0),
      0,
    );
  }, [orderCountByStatus, visibleStatuses]);

  const workflowNavigationItems = useMemo(
    () => getWorkflowNavigationItems(userWorkflow, visibleStatuses, allStatusOptions, t),
    [userWorkflow, visibleStatuses, allStatusOptions, t],
  );

  const displayedOrders = useMemo(() => {
    return orders.filter((order) => {
      const orderStatus = normalizeStatus(order.status);
      const orderNumber = String(order.order_number || '').toLowerCase();
      const customerName = String(order.customer_name || '').toLowerCase();
      const customerPhone = String(order.customer_phone || '').toLowerCase();
      const tableNumber = String(order.table_number || '').toLowerCase();
      const normalizedSearch = filters.search.trim().toLowerCase();

      const matchesRoleStatus = visibleStatuses.includes(orderStatus);
      const matchesSelectedStatus =
        !filters.status || orderStatus === filters.status;
      const matchesOrderType =
        !filters.order_type || order.order_type === filters.order_type;
      const matchesSearch =
        !normalizedSearch ||
        orderNumber.includes(normalizedSearch) ||
        customerName.includes(normalizedSearch) ||
        customerPhone.includes(normalizedSearch) ||
        tableNumber.includes(normalizedSearch);

      return (
        matchesRoleStatus &&
        matchesSelectedStatus &&
        matchesOrderType &&
        matchesSearch
      );
    });
  }, [orders, filters, visibleStatuses]);

  const totalPages = Math.max(1, Math.ceil(displayedOrders.length / ordersPerPage));
  const activePage = Math.min(currentPage, totalPages);

  const firstOrderIndex = (activePage - 1) * ordersPerPage;
  const paginatedOrders = displayedOrders.slice(
    firstOrderIndex,
    firstOrderIndex + ordersPerPage,
  );
  const paginationStart = displayedOrders.length > 0 ? firstOrderIndex + 1 : 0;
  const paginationEnd = Math.min(
    firstOrderIndex + ordersPerPage,
    displayedOrders.length,
  );

  const pageNumbers = useMemo(() => {
    const maxVisiblePages = 5;
    const firstPage = Math.max(
      1,
      Math.min(activePage - 2, totalPages - maxVisiblePages + 1),
    );
    const lastPage = Math.min(totalPages, firstPage + maxVisiblePages - 1);

    return Array.from(
      { length: lastPage - firstPage + 1 },
      (_, index) => firstPage + index,
    );
  }, [activePage, totalPages]);

  const pendingOrders = useMemo(
    () =>
      displayedOrders.filter(
        (order) => normalizeStatus(order.status) === 'pending',
      ).length,
    [displayedOrders],
  );

  const totalAmount = useMemo(() => {
    return displayedOrders.reduce((total, order) => {
      return total + Number(order.total_amount || 0);
    }, 0);
  }, [displayedOrders]);

  const currency = displayedOrders[0]?.currency || orders[0]?.currency || 'MAD';

  const fetchMenus = async () => {
    try {
      setMenusLoading(true);

      const response = await menusApi.getMenus(
        establishmentId,
        {
          per_page: 100,
          locale: language,
        },
      );
      const responseMenus =
        response.data?.data?.menus ||
        response.data?.data ||
        [];

      const normalizedMenus = Array.isArray(responseMenus)
        ? responseMenus
        : [];

      setMenus(normalizedMenus);

      const storedMenuId = localStorage.getItem(
        SELECTED_MENU_STORAGE_KEY,
      );

      const storedMenuExists = normalizedMenus.some(
        (menu) => String(menu.id) === String(storedMenuId),
      );

      if (storedMenuExists) {
        setSelectedMenuId(String(storedMenuId));
        return;
      }

      const defaultMenu =
        normalizedMenus.find((menu) => Boolean(menu.is_default)) ||
        normalizedMenus.find((menu) => Boolean(menu.is_active)) ||
        normalizedMenus[0];

      if (defaultMenu?.id) {
        const menuId = String(defaultMenu.id);

        localStorage.setItem(
          SELECTED_MENU_STORAGE_KEY,
          menuId,
        );

        setSelectedMenuId(menuId);
      } else {
        localStorage.removeItem(
          SELECTED_MENU_STORAGE_KEY,
        );

        setSelectedMenuId('');
      }
    } catch (error) {
      setMenus([]);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: getApiErrorMessage(error),
      });
    } finally {
      setMenusLoading(false);
    }
  };

  const fetchOrders = async () => {
    if (!selectedMenuId) {
      setOrders([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response = await ordersApi.getOrders(establishmentId, {
        menu_id: selectedMenuId,
        search: filters.search || undefined,
        order_type: filters.order_type || undefined,
        locale: language,
        per_page: 100,
      });

      setOrders(response.data.data || []);
    } catch (error) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: getApiErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMenuChange = (menuId) => {
    setSelectedMenuId(menuId);

    setFilters({
      search: '',
      status: '',
      order_type: '',
    });

    setCurrentPage(1);

    if (menuId) {
      localStorage.setItem(
        SELECTED_MENU_STORAGE_KEY,
        menuId,
      );
    } else {
      localStorage.removeItem(
        SELECTED_MENU_STORAGE_KEY,
      );
    }
  };

  useEffect(() => {
    fetchMenus();
  }, [establishmentId, language]);

  useEffect(() => {
    if (filters.status && !visibleStatuses.includes(filters.status)) {
      setFilters((currentFilters) => ({
        ...currentFilters,
        status: '',
      }));
    }
  }, [filters.status, visibleStatuses]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.search, filters.status, filters.order_type, ordersPerPage, userWorkflow]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [activePage, totalPages]);

  useEffect(() => {
    fetchOrders();
  }, [filters.order_type, userWorkflow, selectedMenuId, language]);

  const submitSearch = (event) => {
    event.preventDefault();
    fetchOrders();
  };

  const updateStatus = async (orderId, status) => {
    try {
      setUpdatingOrderId(orderId);

      await ordersApi.updateOrderStatus(orderId, status);
      await fetchOrders();

      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: t('orders.alerts.statusUpdated'),
      });
    } catch (error) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: getApiErrorMessage(error),
      });
    } finally {
      setUpdatingOrderId(null);
    }
  };


  const openOrderModal = async (order) => {
    try {
      setSelectedOrderLoading(true);
      setSelectedOrder(order);
      setSelectedStatus(normalizeStatus(order.status));

      const response = await ordersApi.getOrder(order.id);
      const fullOrder = response.data?.data || order;

      setSelectedOrder(fullOrder);
      setSelectedStatus(normalizeStatus(fullOrder.status));
    } catch (error) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: getApiErrorMessage(error),
      });
    } finally {
      setSelectedOrderLoading(false);
    }
  };

  const closeOrderModal = () => {
    if (updatingOrderId) return;

    setSelectedOrder(null);
    setSelectedStatus('');
  };

  const saveSelectedOrderStatus = async () => {
    if (!selectedOrder?.id || !selectedStatus) return;

    const currentStatus = normalizeStatus(selectedOrder.status);

    if (selectedStatus === currentStatus) {
      closeOrderModal();
      return;
    }

    try {
      setUpdatingOrderId(selectedOrder.id);

      await ordersApi.updateOrderStatus(
        selectedOrder.id,
        selectedStatus,
      );

      await fetchOrders();

      setSelectedOrder((currentOrder) =>
        currentOrder
          ? {
              ...currentOrder,
              status: selectedStatus,
            }
          : currentOrder,
      );

      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: t('orders.alerts.statusUpdated'),
      });
    } catch (error) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: getApiErrorMessage(error),
      });
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const getOrderModalStatusOptions = (order) => {
    if (!order) return [];

    return getOrderStatusOptions(order);
  };

  if (loading) {
    return (
      <main className="orders-page" dir={direction}>
        <div className="orders-loading">
          <Loader2 size={30} />
          <span>{t('orders.loading')}</span>
        </div>
      </main>
    );
  }

  return (
    <main className="orders-page" dir={direction}>
      <section className="orders-heading">
        <div>
          <p>{t('orders.eyebrow')}</p>
          <h1>{t('orders.title')}</h1>
          <span>{t('orders.description')}</span>
        </div>

        <div className="orders-heading__actions">
          {canCreateOrders && selectedMenuId && (
            <Link to="/orders/new" className="orders-add-button">
              <Plus size={18} />
              {t('orders.form.title')}
            </Link>
          )}

          <div className="orders-heading__icon">
            <ShoppingBag size={30} />
          </div>
        </div>
      </section>

      <section className="orders-menu-context">
        <div className="orders-menu-context__icon">
          <BookOpen size={22} />
        </div>

        <div className="orders-menu-context__content">
          <span>{t('menus.title')}</span>
          <strong>
            {getTranslatedField(
              selectedMenu,
              'name',
              language,
              'fr',
            ) || '—'}
          </strong>

          {getTranslatedField(
            selectedMenu,
            'description',
            language,
            'fr',
          ) ? (
            <small>
              {getTranslatedField(
                selectedMenu,
                'description',
                language,
                'fr',
              )}
            </small>
          ) : null}
        </div>

        <CustomSelect
          value={selectedMenuId}
          options={menus.map((menu) => ({
            value: String(menu.id),
            label: `${getTranslatedField(
              menu,
              'name',
              language,
              'fr',
            ) || '—'}${
              menu.is_default
                ? ` • ${t(
                    'menus.status.default',
                  )}`
                : ''
            }`,
          }))}
          placeholder={
            menusLoading
              ? t('menus.loading')
              : t('menus.empty.title')
          }
          className="orders-custom-select--menu"
          ariaLabel={t('menus.title')}
          disabled={menusLoading || menus.length === 0}
          onChange={handleMenuChange}
        />
      </section>

      <section className="orders-stats">
        <article>
          <strong>{formatNumber(displayedOrders.length, language)}</strong>
          <span>{t('orders.stats.total')}</span>
        </article>

        <article>
          <strong>{formatNumber(pendingOrders, language)}</strong>
          <span>{t('orders.stats.pending')}</span>
        </article>

        <article>
          <strong>
            {formatMoney(
              totalAmount,
              currency,
              language,
            )}
          </strong>
          <span>{t('orders.stats.revenue')}</span>
        </article>
      </section>

      <section className={`orders-workflow-navigation orders-workflow-navigation--${userWorkflow}`}>
        {workflowNavigationItems.map((item) => {
          const NavigationIcon = item.icon;
          const itemCount = item.value
            ? Number(orderCountByStatus[item.value] || 0)
            : visibleOrdersCount;

          return (
            <button
              key={item.value || 'all'}
              type="button"
              className={filters.status === item.value ? 'active' : ''}
              onClick={() =>
                setFilters((currentFilters) => ({
                  ...currentFilters,
                  status: item.value,
                }))
              }
            >
              <span className="orders-workflow-navigation__icon">
                <NavigationIcon size={18} />
              </span>

              <span className="orders-workflow-navigation__content">
                <strong>{item.label}</strong>
                <small>{item.description}</small>
              </span>

              <em>{formatNumber(itemCount, language)}</em>
            </button>
          );
        })}
      </section>

      <section className="orders-panel">
        <form className="orders-toolbar" onSubmit={submitSearch}>
          <label className="orders-search">
            <Search size={18} />

            <input
              value={filters.search}
              disabled={!selectedMenuId}
              placeholder={t('orders.filters.searchPlaceholder')}
              aria-label={t('orders.filters.searchPlaceholder')}
              onChange={(event) =>
                setFilters((currentFilters) => ({
                  ...currentFilters,
                  search: event.target.value,
                }))
              }
            />
          </label>

          <CustomSelect
            value={filters.status}
            options={statusOptions}
            className="orders-custom-select--toolbar"
            ariaLabel={t('orders.filters.status')}
            disabled={!selectedMenuId}
            onChange={(nextStatus) =>
              setFilters((currentFilters) => ({
                ...currentFilters,
                status: nextStatus,
              }))
            }
          />

          <CustomSelect
            value={filters.order_type}
            options={typeOptions}
            className="orders-custom-select--toolbar"
            ariaLabel={t('orders.filters.type')}
            disabled={!selectedMenuId}
            onChange={(nextType) =>
              setFilters((currentFilters) => ({
                ...currentFilters,
                order_type: nextType,
              }))
            }
          />

          <button type="submit" disabled={!selectedMenuId}>
            {t('orders.filters.apply')}
          </button>
        </form>

        <div className="orders-grid" aria-label={t('orders.title')}>
          {paginatedOrders.map((order) => {
            const currentStatus = normalizeStatus(order.status);
            const orderDate =
              formatDateTime(
                order.created_at,
                language,
              );

            return (
              <article
                key={order.id}
                className={`orders-order-card orders-order-card--${currentStatus}`}
              >
                <button
                  type="button"
                  className="orders-order-card__main"
                  onClick={() => openOrderModal(order)}
                >
                  <span className="orders-order-card__top">
                    <span
                      className="orders-order-card__number"
                      dir="ltr"
                    >
                      {order.order_number}
                    </span>

                    <span
                      className={`orders-order-card__status orders-order-card__status--${currentStatus}`}
                    >
                      {t(`orders.status.${currentStatus}`)}
                    </span>
                  </span>

                  <span className="orders-order-card__customer">
                    <strong>{order.customer_name || '-'}</strong>

                    {order.order_type === 'dine_in' &&
                    order.table_number ? (
                      <small>
                        {t('orders.table.tableNumber')}: {order.table_number}
                      </small>
                    ) : (
                      <small dir="ltr">{order.customer_phone || '-'}</small>
                    )}
                  </span>

                  <span className="orders-order-card__meta">
                    <span>
                      <small>{t('orders.table.type')}</small>
                      <strong>
                        {t(`orders.types.${order.order_type}`)}
                      </strong>
                    </span>

                    <span>
                      <small>{t('orders.table.total')}</small>
                      <strong>
                        {formatMoney(
                          order.total_amount,
                          order.currency || 'MAD',
                          language,
                        )}
                      </strong>
                    </span>
                  </span>

                  <span className="orders-order-card__footer">
                    <span>
                      <Clock3 size={15} />
                      {orderDate}
                    </span>

                    <strong>
                      {t('orders.detailsTitle')}
                    </strong>
                  </span>
                </button>

                <Link
                  to={`/orders/${order.id}`}
                  className="orders-order-card__details-link"
                >
                  <ReceiptText size={17} />

                  {t('orders.detailsTitle')}
                </Link>
              </article>
            );
          })}

          {!displayedOrders.length && (
            <div className="orders-card-empty">
              {selectedMenuId
                ? t('orders.empty')
                : t('menus.empty.description')}
            </div>
          )}
        </div>

        {displayedOrders.length > 0 && (
          <div className="orders-pagination">
            <div className="orders-pagination__summary">
              <span>{t('orders.pagination.showing')}</span>
              <strong>
                {formatNumber(paginationStart, language)} - {formatNumber(paginationEnd, language)}
              </strong>
              <span>{t('orders.pagination.of')}</span>
              <strong>{formatNumber(displayedOrders.length, language)}</strong>
            </div>

            <div className="orders-pagination__controls">
              <CustomSelect
                value={String(ordersPerPage)}
                options={paginationSizeOptions}
                className="orders-custom-select--pagination"
                ariaLabel={t('orders.pagination.page')}
                onChange={(nextPageSize) => {
                  setOrdersPerPage(Number(nextPageSize));
                  setCurrentPage(1);
                }}
              />

              <button
                type="button"
                className="orders-pagination__button"
                disabled={activePage <= 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              >
                <ChevronLeft size={17} />
                {t('orders.pagination.previous')}
              </button>

              <div className="orders-pagination__pages">
                {pageNumbers.map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    className={activePage === pageNumber ? 'active' : ''}
                    onClick={() => setCurrentPage(pageNumber)}
                  >
                    {formatNumber(pageNumber, language)}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="orders-pagination__button"
                disabled={activePage >= totalPages}
                onClick={() =>
                  setCurrentPage((page) => Math.min(totalPages, page + 1))
                }
              >
                {t('orders.pagination.next')}
                <ChevronRight size={17} />
              </button>
            </div>
          </div>
        )}
      </section>

      {selectedOrder && (
        <div className="orders-modal">
          <button
            type="button"
            className="orders-modal__overlay"
            onClick={closeOrderModal}
            aria-label={t('common.close')}
          />

          <section className="orders-modal__content">
            <header className="orders-modal__header">
              <div>
                <p dir="ltr">{selectedOrder.order_number}</p>
                <h2>
                  {t('orders.detailsTitle')}
                </h2>
              </div>

              <button
                type="button"
                className="orders-modal__close"
                onClick={closeOrderModal}
                disabled={Boolean(updatingOrderId)}
                aria-label={t('common.close')}
              >
                <X size={20} />
              </button>
            </header>

            {selectedOrderLoading ? (
              <div className="orders-modal__loading">
                <Loader2 size={30} />
                <span>{t('orders.loading')}</span>
              </div>
            ) : (
              <>
                <div className="orders-modal__body">
                  <section className="orders-modal__summary">
                    <article>
                      <span>
                        <UserRound size={18} />
                      </span>

                      <div>
                        <small>{t('orders.table.customer')}</small>
                        <strong>
                          {selectedOrder.customer_name || '-'}
                        </strong>
                      </div>
                    </article>

                    <article>
                      <span>
                        <Phone size={18} />
                      </span>

                      <div>
                        <small>{t('orders.table.phone')}</small>
                        <strong dir="ltr">
                          {selectedOrder.customer_phone || '-'}
                        </strong>
                      </div>
                    </article>

                    <article>
                      <span>
                        <ShoppingBag size={18} />
                      </span>

                      <div>
                        <small>{t('orders.table.type')}</small>
                        <strong>
                          {t(
                            `orders.types.${selectedOrder.order_type}`,
                          )}
                        </strong>
                      </div>
                    </article>

                    {selectedOrder.order_type === 'dine_in' && (
                      <article>
                        <span>
                          <ReceiptText size={18} />
                        </span>

                        <div>
                          <small>
                            {t('orders.table.tableNumber')}
                          </small>
                          <strong>
                            {selectedOrder.table_number || '-'}
                          </strong>
                        </div>
                      </article>
                    )}
                  </section>

                  <section className="orders-modal__section">
                    <div className="orders-modal__section-head">
                      <div>
                        <p>
                          {translateFallback(
                            t,
                            'orders.details.itemsTitle',
                            'Order items',
                          )}
                        </p>

                        <h3>
                          {translateFallback(
                            t,
                            'orders.details.itemsDescription',
                            'Products included in this order',
                          )}
                        </h3>
                      </div>

                      <strong>
                        {formatNumber((selectedOrder.items || []).length, language)}
                      </strong>
                    </div>

                    <div className="orders-modal__items">
                      {(selectedOrder.items || []).map((item) => (
                        <article
                          key={item.id}
                          className="orders-modal-item"
                        >
                          <div className="orders-modal-item__top">
                            <strong>
                              {item.product_name || '-'}
                            </strong>

                            <span>
                              {formatMoney(
                                item.total_price,
                                selectedOrder.currency || 'MAD',
                                language,
                              )}
                            </span>
                          </div>

                          <div className="orders-modal-item__meta">
                            {item.size_name && (
                              <span>
                                {translateFallback(
                                  t,
                                  'orders.details.size',
                                  'Size',
                                )}: {item.size_name}
                              </span>
                            )}

                            <span>
                              {translateFallback(
                                t,
                                'orders.details.quantity',
                                'Quantity',
                              )}: {formatNumber(
                                  item.quantity,
                                  language,
                                )}
                            </span>

                            <span>
                              {translateFallback(
                                t,
                                'orders.details.price',
                                'Price',
                              )}: {formatMoney(
                                  item.unit_price,
                                  selectedOrder.currency || 'MAD',
                                  language,
                                )}
                            </span>
                          </div>

                          {item.note && (
                            <small>{item.note}</small>
                          )}
                        </article>
                      ))}

                      {!(selectedOrder.items || []).length && (
                        <div className="orders-modal__empty">
                          {t('orders.empty')}
                        </div>
                      )}
                    </div>
                  </section>

                  <section className="orders-modal__section">
                    <div className="orders-modal__section-head">
                      <div>
                        <p>
                          {translateFallback(
                            t,
                            'orders.table.status',
                            'Status',
                          )}
                        </p>

                        <h3>
                          {t('orders.details.changeStatus')}
                        </h3>
                      </div>
                    </div>

                    <div className="orders-status-radios">
                      {getOrderModalStatusOptions(
                        selectedOrder,
                      ).map((option) => {
                        const active =
                          selectedStatus === option.value;

                        return (
                          <label
                            key={option.value}
                            className={`orders-status-radio orders-status-radio--${option.value} ${
                              active ? 'is-active' : ''
                            }`}
                          >
                            <input
                              type="radio"
                              name={`order-status-${selectedOrder.id}`}
                              value={option.value}
                              checked={active}
                              disabled={Boolean(updatingOrderId)}
                              onChange={() =>
                                setSelectedStatus(option.value)
                              }
                            />

                            <span className="orders-status-radio__dot" />

                            <span>
                              {option.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </section>
                </div>

                <footer className="orders-modal__footer">
                  <div>
                    <span>{t('orders.table.total')}</span>
                    <strong>
                      {formatMoney(
                        selectedOrder.total_amount,
                        selectedOrder.currency || 'MAD',
                        language,
                      )}
                    </strong>
                  </div>

                  <div className="orders-modal__footer-actions">
                    <Link
                      to={`/orders/${selectedOrder.id}`}
                      className="orders-modal__details-link"
                    >
                      <ReceiptText size={17} />

                      {t('orders.detailsTitle')}
                    </Link>

                    <button
                      type="button"
                      className="orders-modal__save"
                      onClick={saveSelectedOrderStatus}
                      disabled={Boolean(updatingOrderId)}
                    >
                    {updatingOrderId ? (
                      <Loader2 size={18} />
                    ) : (
                      <Check size={18} />
                    )}

                    {t('orders.details.updateStatus')}
                    </button>
                  </div>
                </footer>
              </>
            )}
          </section>
        </div>
      )}

    </main>
  );
}

export default Orders;